import { useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrayParam, StringParam, useQueryParams } from 'use-query-params'

import { useVerificationTabs } from '../useVerificationTabs'
import { AgentVerificationBulkRejectModal } from '../../agentVerification/agentVerificationBulkRejectModal/AgentVerificationBulkRejectModal'

import { HeldValidationSubmission } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { useCSVDownloader, usePermissions } from '@percent/admin-dashboard/common/hooks'
import { useValidationSubmissionsAccess } from '@percent/admin-dashboard/common/hooks/useValidationSubmissionsAccess/useValidationSubmissionsAccess'
import { getDate } from '@percent/admin-dashboard/common/utility'
import { getCountryFromAlpha3 } from '@percent/admin-dashboard/common/utility/getCountryFromCountryCode'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  Button,
  Flex,
  FlagAvatar,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Tabs,
  Tooltip
} from '@percent/lemonade'
import { useQueryList } from '@percent/utility'
import { BulkReleaseHoldsModal } from './bulkReleaseHoldsModal/BulkReleaseHoldsModal'
import { HeldValidationSubmissionsFilters } from './heldValidationSubmissionsFilters/HeldValidationSubmissionsFilters'
import { ReleaseHoldModal } from './releaseHoldModal/ReleaseHoldModal'

export function HeldValidationSubmissionsContainer() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { validationSubmissionsService } = useServices()
  const { canReleaseHold } = useValidationSubmissionsAccess()
  const { userHasPermissions } = usePermissions()
  const canBulkReject = userHasPermissions('agent_verifications_bulk_rejection:can_bulk_reject')
  const [bulkAction, setBulkAction] = useState<'reject' | 'release' | null>(null)
  const verificationTabs = useVerificationTabs()
  const [selected, setSelected] = useState<HeldValidationSubmission | null>(null)
  const [queryParams, setQueryParams] = useQueryParams({
    query: StringParam,
    partnerId: StringParam,
    configurationIds: ArrayParam
  })

  const [{ data, totalResults, isLoading, error, errorMessage }, { nextPage, previousPage, refresh }] = useQueryList(
    ['/v1/admin/validation-submissions/holds'],
    validationSubmissionsService.getHeldValidationSubmissions,
    queryParams
  )

  const [{ loading: isDownloading }, { downloadCSV }] = useCSVDownloader({
    service: () => validationSubmissionsService.downloadHeldValidationSubmissionsCsv(queryParams),
    fileName: `held-validation-submissions-${dayjs().format('YYYY-MM-DD')}`,
    successMessage: t('toast.exportHeldValidationSubmissionsSuccess'),
    errorMessage: t('toast.exportHeldValidationSubmissionsError')
  })

  const isFiltered = !!queryParams.query || !!queryParams.partnerId || !!queryParams.configurationIds?.length

  // `errorMessage` is also set, to "No results", for an empty list - that is the table's empty state, not an error.
  if (error && errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const columns: TableColumn[] = [
    { id: 'country', content: t('table.country') },
    { id: 'agentName', content: t('table.agentName') },
    { id: 'agentEmail', content: t('table.agentEmail') },
    { id: 'partner', content: t('table.partner') },
    { id: 'configuration', content: t('table.configuration') },
    { id: 'receivedAt', content: t('table.receivedAt') },
    { id: 'heldAt', content: t('table.heldAt') },
    { id: 'actions', content: t('table.actions') },
    { id: 'open', content: '' }
  ]

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults ?? 0}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t(isFiltered ? 'typography.noSearchResults' : 'typography.noHeldValidationSubmissions')}
      >
        {verificationTabs.items && (
          <Tabs
            items={verificationTabs.items}
            activeTab={verificationTabs.active}
            onChangeTab={verificationTabs.onChangeTab}
          />
        )}
        <TableSearchAndFilters>
          <HeldValidationSubmissionsFilters queryParams={queryParams} setQueryParams={setQueryParams} />
          <Flex gap="xs" ml="auto" align="flex-start">
            <Button size="small" variant="secondary" loading={!!isDownloading} onClick={() => downloadCSV?.()}>
              {t('button.downloadCsv')}
            </Button>
            {canBulkReject && (
              <Button size="small" variant="secondary" onClick={() => setBulkAction('reject')}>
                {t('button.bulkRejectAgentVerifications')}
              </Button>
            )}
            {canReleaseHold && (
              <Button size="small" variant="primary" onClick={() => setBulkAction('release')}>
                {t('button.bulkReleaseHolds')}
              </Button>
            )}
          </Flex>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {data?.map(row => {
              const name = [row.submission.firstName, row.submission.lastName].filter(Boolean).join(' ')

              return (
                <TableRow key={row.id} onClick={handleClickAsLink(`/validation-submissions/${row.id}`, navigate)}>
                  <TableCell>
                    {row.submission.countryCode ? (
                      <Tooltip content={getCountryFromAlpha3(row.submission.countryCode)}>
                        <FlagAvatar code3={row.submission.countryCode} />
                      </Tooltip>
                    ) : (
                      t('status.na')
                    )}
                  </TableCell>
                  <TableCell>{name || row.id}</TableCell>
                  <TableCell>{row.submission.email || t('status.na')}</TableCell>
                  <TableCell>{row.partner.name ?? row.partner.id}</TableCell>
                  <TableCell>{row.configuration.name || row.configuration.id}</TableCell>
                  <TableCell>{getDate(row.receivedAt)}</TableCell>
                  <TableCell>{getDate(row.heldAt)}</TableCell>
                  <TableCell>
                    {canReleaseHold && (
                      <Button size="small" variant="secondary" onClick={() => setSelected(row)}>
                        {t('button.releaseHold')}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Icon name="chevron-right" color="gray400" size={4} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePagination
          previousPage={previousPage}
          nextPage={nextPage}
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
        />
      </TableProvider>
      <ReleaseHoldModal submission={selected} onClose={() => setSelected(null)} onReleased={refresh} />
      {bulkAction === 'reject' && (
        <AgentVerificationBulkRejectModal
          onClose={() => {
            setBulkAction(null)
            refresh()
          }}
        />
      )}
      {bulkAction === 'release' && <BulkReleaseHoldsModal onClose={() => setBulkAction(null)} onReleased={refresh} />}
    </>
  )
}
